/**
 * Mutually exclusive UI phase observations. Consumers dedupe these in one
 * shared slot per swap, so a real A → B → A recovery is still delivered while
 * a replay of A alone is not.
 */
export const SWAP_LIFECYCLE_PHASE_STEPS = [
    'awaiting_wallet_action', 'awaiting_user_deposit', 'input_transfer_pending', 'output_transfer_pending',
    'output_settling', 'swap_completed', 'swap_failed', 'swap_delayed', 'swap_expired', 'swap_cancelled',
    'refund_pending', 'refund_completed',
];
/** Each on-chain transaction observation owns its own dedupe slot per swap. */
export const SWAP_LIFECYCLE_TRANSACTION_STEPS = [
    'input_transaction_detected', 'input_transfer_confirmed', 'output_transaction_detected',
];
/**
 * Every other step: user actions, attempts, their results and diagnostics.
 * These are never slotted, so every emission is delivered by every consumer.
 */
export const SWAP_LIFECYCLE_REPEATABLE_STEPS = [
    'form_submitted', 'form_confirmation_cancelled',
    'swap_creation_started', 'swap_created', 'swap_creation_failed',
    'wallet_connection_started', 'wallet_connected', 'wallet_connection_failed',
    'network_switch_started', 'network_switched', 'network_switch_rejected', 'network_switch_failed',
    'wallet_prompt_opened', 'wallet_action_rejected', 'wallet_action_failed',
    'transaction_submitted', 'gasless_authorization_submitted', 'gasless_authorization_failed', 'deposit_address_copied',
    'retry_requested', 'transfer_blocked', 'flow_closed', 'flow_error', 'suspected_stall',
];
/**
 * User or application actions that begin a new attempt. Earlier observations
 * for the same swap no longer suppress later ones once any of these is seen.
 * Always a subset of the repeatable steps.
 */
export const SWAP_LIFECYCLE_ATTEMPT_START_STEPS = [
    'swap_creation_started', 'wallet_connection_started', 'network_switch_started',
    'wallet_prompt_opened', 'retry_requested',
];
/**
 * Steps that show a widget instance watching a swap before its outcome: it
 * created the swap, showed it awaiting the user's transfer, or the user acted
 * on it. A swap never seen in one of these (opened from a URL or history) is
 * only being viewed, so its current phase is not an outcome of this session.
 */
export const SWAP_LIFECYCLE_TRACKING_STEPS = [
    'swap_created', 'awaiting_wallet_action', 'awaiting_user_deposit', ...SWAP_LIFECYCLE_ATTEMPT_START_STEPS,
];
